import { cn } from '../lib/cn.js';
import { token, type TokenName } from '../theme/tokens.js';

export type StatusTone = 'blue' | 'amber' | 'alert' | 'success' | 'muted';

export interface StatusDotProps {
  tone?: StatusTone;
  size?: 'sm' | 'md';
  /**
   * Pulse the dot. **Only for genuinely live signals** — the design system
   * restricts looping animation to live state, and the animation is suppressed
   * under `prefers-reduced-motion`.
   */
  pulse?: boolean;
  /**
   * Accessible label. Provide one when the dot is the *only* carrier of the
   * status; omit it when adjacent text already says the same thing, so screen
   * readers do not hear it twice.
   */
  label?: string;
  className?: string;
}

/**
 * Which token each tone is painted from.
 *
 * **One map, exported, because a second copy is a tile that disagrees with its
 * own dot.** `StatusCell` washes its whole background from the same tone the
 * dot inside it is showing, and `NocSurface` paints a meter's bar from it; all
 * three read this rather than restating it.
 */
const TONE_TOKEN: Record<StatusTone, TokenName> = {
  blue: 'bp-blue',
  amber: 'bp-amber',
  alert: 'bp-danger',
  success: 'bp-success',
  muted: 'bp-text-faint',
};

/** The tone at full strength — a dot, a bar, a rule. */
export function toneColor(tone: StatusTone): string {
  return token(TONE_TOKEN[tone]);
}

/**
 * The tone as a wash over whatever surface is behind it.
 *
 * **Mixed toward `transparent`, never toward white** — the design system's rule
 * for tints, and the reason this reads as a *darker* version of the tone on the
 * command palette rather than a pastel one. `Badge`'s own `tint()` is the same
 * expression at a smaller percentage.
 */
export function toneWash(tone: StatusTone, percent: number): string {
  return `color-mix(in srgb, ${toneColor(tone)} ${String(percent)}%, transparent)`;
}

/**
 * Status is communicated with colored dots and amber labels — never emoji.
 * (`design/design-system/readme.md`: *"No emoji. Ever. This is a duty tool."*)
 */
/** Tailwind's 0.25rem scale: size-1.5 = 6px, size-2 = 8px. No raw px. */
const SIZE_CLASS: Record<NonNullable<StatusDotProps['size']>, string> = {
  sm: 'size-1.5',
  md: 'size-2',
};

export function StatusDot({
  tone = 'muted',
  size = 'md',
  pulse = false,
  label,
  className,
}: StatusDotProps) {
  return (
    <span
      {...(label !== undefined ? { role: 'status', 'aria-label': label } : { 'aria-hidden': true })}
      className={cn(
        'inline-block shrink-0 rounded-full',
        SIZE_CLASS[size],
        pulse && 'bp-pulse',
        className,
      )}
      style={{ background: toneColor(tone) }}
    />
  );
}
